<template>
  <div>
    <section class="hero is-primary is-bold">
      <div class="hero-body">
        <div class="container is-fluid licitacoes-lista__container">
          <div class="level">
            <div class="level-left"><h1 class="title">Listagem de Licitações</h1></div>
            <div v-if="canCreate" class="level-right">
              <div class="level-item licitacoes-lista__criar-group" data-tour="licitacoes-criar">
                <b-dropdown
                  class="licitacoes-lista__criar"
                  :triggers="['hover', 'click']"
                  aria-role="list"
                  position="is-bottom-left"
                >
                  <template #trigger>
                    <button class="button is-primary is-inverted is-outlined">
                      <span>Criar Licitação</span>
                      <b-icon icon="menu-down" size="is-small" />
                    </button>
                  </template>
                  <b-dropdown-item
                    v-for="action in createActions"
                    :key="action.type"
                    aria-role="listitem"
                    @click="createOfertaCompra(action.type)"
                  >
                    {{ action.label }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section licitacoes-lista__content">
      <div class="container is-fluid licitacoes-lista__container">
        <FiltrosLicitacoesPanel
          v-model="panelFilters"
          :persona="persona"
          :applied="filters"
          :loading="loading"
          :purchasing-units="buyerPurchasingUnits"
          :purchasing-units-loading="purchasingUnitFilterOptionsLoading"
          @filter="applyFilters"
          @clear="clearFilters"
          @chip="applyFilters"
        >
          <template #heading-extra>
            <FeatureTourHelp
              class="licitacoes-lista__tour-help"
              :icon-only="true"
              label=""
              icon="help-circle"
              icon-size="is-medium"
              trigger-class="has-text-link"
              contexto="licitacoes.lista"
              :feature-ctx="ctxTourLicitacoes()"
              scope="filtros"
              @selecionar="reabrirTutorialLicitacoes"
            />
          </template>
        </FiltrosLicitacoesPanel>

        <div class="panel licitacoes-lista__results">
          <div
            v-if="items.length"
            class="panel-heading is-flex is-align-items-center is-justify-content-space-between"
          >
            <span>Resultados</span>
          </div>
          <div class="panel-block is-block licitacoes-lista__table-wrap">
            <section v-if="!items.length && !loading" class="section">
              <div class="content has-text-grey has-text-centered">Nenhum registro encontrado.</div>
            </section>
            <b-table
              v-else
              detailed
              class="table is-fullwidth tabela-licitacoes"
              :data="items"
              :mobile-cards="true"
              :loading="loading"
              @details-open="onDetailsOpen"
            >
              <b-table-column
                centered
                label="Nº Licitação/Processo"
                field="numeroProcesso"
                width="13%"
                cell-class="licitacoes-lista__licitacao-cell"
                header-class="licitacoes-lista__licitacao-cell"
                v-slot="props"
              >
                <router-link
                  v-if="props.row.situacao && props.row.situacao.id > 1"
                  :to="{ name: getRouter(props.row), params: { id: props.row.id } }"
                >
                  {{ props.row.numero_oc || props.row.numeroLicitacao }}
                </router-link>
                <span v-else>{{ props.row.numero_oc || props.row.numeroLicitacao }}</span>
                <p class="has-text-grey is-size-7">
                  {{ props.row.numero_processo || props.row.numeroProcesso }}
                </p>
              </b-table-column>
              <b-table-column
                centered
                label="Modalidade"
                field="modalidade"
                width="14%"
                cell-class="licitacoes-lista__modalidade-cell"
                header-class="licitacoes-lista__modalidade-cell"
                v-slot="props"
              >
                {{ modalidadeLabel(props.row.modalidade || props.row.tipo) }}
              </b-table-column>
              <b-table-column
                v-if="persona !== 'pregoeiro'"
                centered
                label="Órgão"
                field="comprador.nome"
                width="16%"
                cell-class="licitacoes-lista__comprador-cell"
                header-class="licitacoes-lista__comprador-cell"
                v-slot="props"
              >
                {{ props.row.comprador && props.row.comprador.nome }}
              </b-table-column>
              <b-table-column
                v-else
                centered
                label="Unidade de compra"
                field="purchasingUnit.name"
                width="16%"
                cell-class="licitacoes-lista__comprador-cell"
                header-class="licitacoes-lista__comprador-cell"
                v-slot="props"
              >
                <PurchasingUnitLabel :unit="props.row.purchasingUnit" />
              </b-table-column>
              <b-table-column
                centered
                label="Situação"
                field="situacao.nome"
                width="22%"
                cell-class="licitacoes-lista__situacao-cell"
                header-class="licitacoes-lista__situacao-cell"
                v-slot="props"
              >
                <div>{{ props.row.situacao && props.row.situacao.nome }}</div>
                <div
                  v-if="props.row.suspensa || prevAberturaLabel(props.row)"
                  class="licitacoes-lista__situacao-chips"
                >
                  <b-tag v-if="props.row.suspensa" type="is-warning" size="is-small"
                    >Suspensa</b-tag
                  >
                  <b-tag v-else-if="prevAberturaLabel(props.row)" type="is-info" size="is-small">
                    {{ prevAberturaLabel(props.row) }}
                  </b-tag>
                </div>
              </b-table-column>
              <b-table-column
                centered
                label="Objeto"
                field="objeto"
                cell-class="licitacoes-lista__objeto-cell"
                header-class="licitacoes-lista__objeto-cell"
                v-slot="props"
              >
                {{ props.row.objeto }}
              </b-table-column>
              <b-table-column
                v-if="persona === 'pregoeiro'"
                centered
                label="Ações"
                width="65"
                cell-class="licitacoes-lista__acoes-cell is-vcentered"
                header-class="licitacoes-lista__acoes-header is-vcentered"
                v-slot="props"
              >
                <b-tooltip label="Editar" position="is-left">
                  <button
                    v-if="canEditLicitacao(props.row)"
                    type="button"
                    class="button is-primary is-small"
                    aria-label="Editar licitação"
                    @click.prevent.stop="editLicitacao(props.row)"
                  >
                    <b-icon icon="pencil" size="is-small" />
                  </button>
                </b-tooltip>
              </b-table-column>
              <template #detail="props">
                <b-tabs expanded type="is-toggle">
                  <b-tab-item label="Informações">
                    <div class="columns">
                      <div class="column">
                        <ul>
                          <li v-if="props.row.numeroProcesso">
                            <strong>Número de Processo:</strong>
                            {{ props.row.numeroProcesso }}
                          </li>
                          <li>
                            <strong>Comprador:</strong>
                            {{ props.row.comprador && props.row.comprador.nome }}
                          </li>
                          <li>
                            <strong>CNPJ:</strong>
                            {{ props.row.comprador && props.row.comprador.cnpj }}
                          </li>
                          <li v-if="props.row.enderecoEntregaCep">
                            <strong>CEP:</strong>
                            {{ props.row.enderecoEntregaCep }}
                          </li>
                          <li v-if="props.row.enderecoEntrega">
                            <strong>Endereço de Entrega:</strong>
                            {{ formataEnderecoEntrega(props.row) }}
                          </li>
                        </ul>
                      </div>
                      <div class="column">
                        <ul>
                          <template v-if="isDispensaLegado(props.row)">
                            <li v-if="props.row.tipoNegociacao && props.row.tipoNegociacao.nome">
                              <strong>Negociação:</strong>
                              {{ props.row.tipoNegociacao.nome }}
                            </li>
                            <li v-if="props.row.tipoParticipacao">
                              <strong>Tipo de Participação:</strong>
                              {{ formatarTipoParticipacao(props.row.tipoParticipacao) }}
                            </li>
                          </template>
                          <template v-else>
                            <li v-if="props.row.tipoPregao">
                              <strong>Tipo de {{ modalidadeLabel(props.row.modalidade) }}:</strong>
                              {{ formatarTipoPregao(props.row.tipoPregao) }}
                            </li>
                            <li v-if="props.row.modoDisputa">
                              <strong>Modo de Disputa:</strong>
                              {{ formatarModoDisputa(props.row.modoDisputa) }}
                            </li>
                            <li v-if="props.row.tipoNegociacao && props.row.tipoNegociacao.nome">
                              <strong>Negociação:</strong>
                              {{ props.row.tipoNegociacao.nome }}
                            </li>
                            <li v-if="props.row.tipoEdital">
                              <strong>Edital:</strong>
                              {{ formatarTipoEdital(props.row.tipoEdital) }}
                            </li>
                          </template>
                          <li v-if="props.row.dataPublicacao">
                            <strong>Data de Publicação:</strong>
                            {{ props.row.dataPublicacao | formatarDataSemHorario }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <button
                      v-if="props.row.edital"
                      class="button is-primary"
                      :class="{ 'is-loading': loadingEditalId === detailKey(props.row) }"
                      @click="baixarEdital(props.row)"
                    >
                      <b-icon icon="download" />
                      <span>Baixar Edital</span>
                    </button>
                  </b-tab-item>
                  <b-tab-item label="Itens">
                    <b-table
                      paginated
                      per-page="5"
                      class="items-table"
                      :data="props.row.itens || []"
                      :mobile-cards="false"
                    >
                      <b-table-column
                        numeric
                        label="ID"
                        field="identificador"
                        width="40"
                        v-slot="itemProps"
                      >
                        {{ itemProps.row.identificador }}
                      </b-table-column>
                      <b-table-column field="nome" label="Descrição" v-slot="itemProps">
                        {{ itemProps.row.descricao }}
                      </b-table-column>
                      <b-table-column numeric label="Quantidade" width="60" v-slot="itemProps">
                        {{ itemProps.row.quantidade }}
                      </b-table-column>
                      <b-table-column
                        v-if="!isDispensaLegado(props.row)"
                        centered
                        label="Última Proposta"
                        v-slot="itemProps"
                      >
                        {{
                          dataUltimaProposta(
                            itemProps.row.ultimaProposta || itemProps.row.ultima_proposta,
                          )
                        }}
                      </b-table-column>
                    </b-table>
                  </b-tab-item>
                </b-tabs>
              </template>
            </b-table>
          </div>
          <div v-if="items.length" class="panel-block is-block licitacoes-lista__paginacao">
            <AppPagination
              :data-filters="paginationFilters"
              :total="count"
              :is-state-u-r-l-enabled="false"
              @change-page="changePage"
              @change-per-page="changePerPage"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import AppPagination from '@/components/AppPagination';
import FeatureTourHelp from '@/components/FeatureTourHelp';
import FiltrosLicitacoesPanel from '@/components/Licitacoes/FiltrosLicitacoesPanel';
import PurchasingUnitLabel from '@/components/PurchasingUnitLabel';
import { isTourActive } from '@/feature-tours';
import featureTourMixin from '@/mixins/featureTour';
import { fetchItemsLastProposal } from '@/resources/electronicAuctions/proposals';
import { getItens as getItensDispensa } from '@/resources/dispensas';
import { getItens as getItensOfertaCompra } from '@/resources/ofertasCompra';
import { formatarData, formatarDataSemHorario } from '@/utils/format';
import { fileServer } from '@/utils/functions';
import { funcoes, getOfertaCompraEnum } from '@/utils/enumerados';
import {
  defaultLicitacoesFilters,
  parseLicitacoesQuery,
  serializeLicitacoesQuery,
} from '@/utils/licitacoesFiltersQuery';

export default {
  name: 'LicitacoesLista',
  mixins: [featureTourMixin],
  components: { AppPagination, FeatureTourHelp, FiltrosLicitacoesPanel, PurchasingUnitLabel },
  filters: {
    formatarData,
    formatarDataSemHorario,
  },
  data() {
    return {
      panelFilters: defaultLicitacoesFilters(),
      syncingFromRoute: false,
      loadingEditalId: null,
      hasLoadedOnce: false,
    };
  },
  computed: {
    ...mapState('companies', ['company']),
    ...mapState('licitacoesPesquisa', ['items', 'count', 'filters', 'loading']),
    ...mapGetters({
      user: 'getUser',
      logged: 'getLogged',
      funcao: 'getFunction',
      ofertaCompraAnexos: 'obterOfertaCompraAnexos',
      dispensaAnexos: 'obterAnexosDispensa',
    }),
    ...mapGetters('purchasingUnits', {
      purchasingUnitFilterOptions: 'purchasingUnitFilterOptions',
      purchasingUnitFilterOptionsCompany: 'purchasingUnitFilterOptionsCompany',
      purchasingUnitFilterOptionsLoading: 'purchasingUnitFilterOptionsLoading',
    }),
    persona() {
      if (this.company.buyer) return 'pregoeiro';
      if (this.company.seller) return 'licitante';
      return 'visitante';
    },
    canOperatePregao() {
      return (
        this.user &&
        this.user.empresas &&
        this.user.empresas.some(empresa => empresa.id === this.company.id && empresa.operar_pregoes)
      );
    },
    canOperateConcorrencia() {
      return (
        this.user &&
        this.user.empresas &&
        this.user.empresas.some(
          empresa => empresa.id === this.company.id && empresa.operar_concorrencias,
        )
      );
    },
    canOperateDispensa() {
      return (
        this.user &&
        this.user.empresas &&
        this.user.empresas.some(
          empresa => empresa.id === this.company.id && empresa.operar_dispensas,
        )
      );
    },
    canCreate() {
      return (
        this.company.buyer &&
        (this.canOperatePregao || this.canOperateConcorrencia || this.canOperateDispensa)
      );
    },
    createActions() {
      const actions = [];
      if (this.canOperatePregao) {
        actions.push(getOfertaCompraEnum('PREGAO'));
      }
      if (this.canOperateConcorrencia) {
        actions.push(getOfertaCompraEnum('CONCORRENCIA'));
      }
      if (this.canOperateDispensa) {
        actions.push(getOfertaCompraEnum('DISPENSA'));
      }
      return actions;
    },
    paginationFilters() {
      return { page: this.filters.pagina, perPage: this.filters.porPagina };
    },
    buyerFilterCompany() {
      return this.purchasingUnitFilterOptionsCompany || this.company || {};
    },
    buyerPurchasingUnits() {
      const companyId = this.buyerFilterCompany.id;
      return this.purchasingUnitFilterOptions.filter(unit => {
        const unitCompanyId =
          unit.companyId ||
          unit.buyerId ||
          (unit.company && unit.company.id) ||
          (unit.buyer && unit.buyer.id) ||
          unit.orgaoId;
        return unitCompanyId == null || String(unitCompanyId) === String(companyId);
      });
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      deep: true,
      handler(query) {
        const filters = this.filtersForPersona(parseLicitacoesQuery(query));
        // Evita reatribuir o draft do painel quando os critérios já batem —
        // isso preserva o foco/cursor no campo que disparou o debounce.
        const nextQuery = serializeLicitacoesQuery(filters);
        const panelQuery = serializeLicitacoesQuery(this.panelFilters);
        if (JSON.stringify(nextQuery) !== JSON.stringify(panelQuery)) {
          this.panelFilters = filters;
        }
        this.setFilters(filters);
        this.buscarLicitacoes();
      },
    },
    items() {
      this.agendarToursLicitacoes();
    },
    canCreate() {
      this.agendarToursLicitacoes();
    },
    persona(value) {
      if (value === 'pregoeiro') this.loadPurchasingUnitOptions();
    },
  },
  methods: {
    ...mapActions('licitacoesPesquisa', ['pesquisarLicitacoes']),
    ...mapActions('purchasingUnits', ['fetchPurchasingUnitFilterOptions']),
    ...mapActions(['novaOfertaCompra', 'buscarOfertaCompraAnexos', 'buscarAnexosDispensa']),
    setFilters(filters) {
      this.$store.commit('licitacoesPesquisa/setFilters', filters);
    },
    loadPurchasingUnitOptions() {
      return this.fetchPurchasingUnitFilterOptions().catch(() => {
        this.$alerta('Erro ao carregar unidades de compra', 'is-danger');
      });
    },
    filtersForPersona(filters) {
      if (this.persona !== 'pregoeiro') return filters;
      return { ...filters, orgaoId: null };
    },
    async buscarLicitacoes() {
      this.syncingFromRoute = true;
      try {
        await this.pesquisarLicitacoes({ company: this.company });
      } catch (error) {
        this.$alerta('Erro ao buscar licitações', 'is-danger');
      } finally {
        const primeiraCarga = !this.hasLoadedOnce;
        this.hasLoadedOnce = true;
        this.syncingFromRoute = false;
        if (primeiraCarga) this.agendarToursLicitacoes();
      }
    },
    /** @returns {boolean} true se navegou (query mudou); false se ficou na mesma URL. */
    pushFilters(filters) {
      if (this.syncingFromRoute) return false;
      const nextQuery = serializeLicitacoesQuery(filters);
      const currentQuery = serializeLicitacoesQuery(parseLicitacoesQuery(this.$route.query));
      if (JSON.stringify(nextQuery) === JSON.stringify(currentQuery)) return false;
      this.$router.push({ name: this.$route.name, query: nextQuery });
      return true;
    },
    applyFilters(filters) {
      const nextFilters = { ...filters, pagina: 1 };
      const navegou = this.pushFilters(nextFilters);
      if (!navegou) {
        // Query igual à atual: o watcher não dispararia sozinho, então refaz a busca aqui
        // (ex.: clique em "Filtrar" sem alterar nenhum critério).
        this.setFilters(parseLicitacoesQuery(this.$route.query));
        this.buscarLicitacoes();
      }
    },
    clearFilters() {
      const defaults = defaultLicitacoesFilters();
      // Sempre reseta o draft do painel: se a URL já está nos defaults,
      // pushFilters é no-op e o v-model não mudaria sozinho.
      this.panelFilters = defaults;
      this.pushFilters(defaults);
    },
    changePage(pagina) {
      this.pushFilters({ ...this.filters, pagina });
    },
    changePerPage(porPagina) {
      this.pushFilters({ ...this.filters, porPagina, pagina: 1 });
    },
    getRouter(row) {
      return row.origem === 'DISPENSA_LEGADO' ? 'Dispensa' : 'Pregao';
    },
    isDispensaLegado(row) {
      return row.origem === 'DISPENSA_LEGADO';
    },
    isCurrentBuyerRow(row) {
      return String(this.company?.id) === String(row.comprador?.id);
    },
    canEditLicitacao(row) {
      if (this.persona !== 'pregoeiro' || this.isDispensaLegado(row) || !this.logged) return false;
      if (!this.isCurrentBuyerRow(row)) return false;

      const situationId = Number(row.situacao?.id);
      return situationId === 1 || this.funcao === funcoes.PREGOEIRO;
    },
    editLicitacao(row) {
      if (!this.canEditLicitacao(row)) return;
      const situationId = Number(row.situacao?.id);
      const sineDie = row.suspensao_sine_die === true || row.suspensaoSineDie === true;
      const route = situationId <= 3 || sineDie ? 'PregaoEditarDados' : 'PregaoEditarAnexos';
      this.$router.push({ name: route, params: { id: row.id } });
    },
    detailKey(row) {
      return `${row.origem}:${row.id}`;
    },
    modalidadeLabel(modalidade) {
      return (
        { PREGAO: 'Pregão', CONCORRENCIA: 'Concorrência', DISPENSA: 'Dispensa' }[modalidade] ||
        modalidade
      );
    },
    /** Antes da abertura da sessão (Edital Publicado … Aguardando Início; exclui Cadastrada). */
    aindaNaoAbriu(row) {
      const situacaoId = row && row.situacao && Number(row.situacao.id);
      return Number.isFinite(situacaoId) && situacaoId >= 2 && situacaoId <= 4;
    },
    formatPrevAbertura(data) {
      if (!data) return '';
      const date = data instanceof Date ? data : new Date(data);
      if (Number.isNaN(date.getTime())) return '';
      return formatarData(date);
    },
    prevAberturaLabel(row) {
      if (!this.aindaNaoAbriu(row) || !row.dataAbertura) return '';
      const data = this.formatPrevAbertura(row.dataAbertura);
      return data ? `Prev. Abertura: ${data}` : '';
    },
    formatarTipoPregao(tipo) {
      if (tipo === 'IMEDIATO') return 'Contratação Imediata';
      return 'Registro de Preços';
    },
    formatarModoDisputa(modo) {
      if (modo === 'ABERTO') return 'Aberto';
      if (modo === 'FECHADO') return 'Aberto/Fechado';
      return 'Fechado/Aberto';
    },
    formatarTipoEdital(edital) {
      if (edital === 'AMPLO') return 'Participação Ampla';
      if (edital === 'AMPLOCOTA') {
        return 'Quota Reservada para ME/EPP/MEI/Cooperativa c/ Preferência';
      }
      return 'Participação Exclusiva de ME/EPP/MEI/Cooperativa c/ Preferência';
    },
    formatarTipoParticipacao(participacao) {
      if (participacao === 'AMPLA') return 'Ampla';
      return 'Exclusiva de ME/EPP/MEI/Cooperativa c/ Preferência';
    },
    formataEnderecoEntrega(row) {
      if (row.enderecoEntregaComplemento) {
        return `${row.enderecoEntrega}, ${row.enderecoEntregaNumero} - ${row.enderecoEntregaComplemento} - ${row.enderecoEntregaBairro} - ${row.enderecoEntregaMunicipio}-${row.enderecoEntregaUf}`;
      }
      return `${row.enderecoEntrega}, ${row.enderecoEntregaNumero} - ${row.enderecoEntregaBairro} - ${row.enderecoEntregaMunicipio}-${row.enderecoEntregaUf}`;
    },
    dataUltimaProposta(data) {
      if (data) return formatarData(data);
      return 'Nenhuma';
    },
    mapItensResposta(payload) {
      const list = Array.isArray(payload)
        ? payload
        : (payload && payload.data) || (payload && payload.itens) || [];
      return (list || []).map(item => ({
        id: item.id,
        identificador: item.identificador,
        descricao: item.descricao || (item.item && item.item.descricao) || item.nome || '',
        quantidade: item.quantidade,
        ultimaProposta: item.ultimaProposta || item.ultima_proposta || null,
      }));
    },
    async ensureRowItens(row) {
      if (!row || (Array.isArray(row.itens) && row.itens.length)) return;
      try {
        const response = this.isDispensaLegado(row)
          ? await getItensDispensa(row.id)
          : await getItensOfertaCompra(row.id);
        const itens = this.mapItensResposta(response && response.data);
        this.$store.commit('licitacoesPesquisa/setRowItens', {
          licitacaoId: row.id,
          origem: row.origem,
          itens,
        });
      } catch (error) {
        this.$store.commit('licitacoesPesquisa/setRowItens', {
          licitacaoId: row.id,
          origem: row.origem,
          itens: [],
        });
      }
    },
    async onDetailsOpen(row) {
      if (!row) return;
      await this.ensureRowItens(row);
      if (row.origem !== 'OC') return;
      try {
        const { data: response } = await fetchItemsLastProposal(row.id);
        this.$store.commit('licitacoesPesquisa/setUltimaPropostaItens', {
          licitacaoId: row.id,
          itens: (response && response.data) || [],
        });
      } catch (error) {
        // Mantém "Nenhuma" na coluna quando a consulta falha
      }
    },
    async baixarEdital(row) {
      const key = this.detailKey(row);
      this.loadingEditalId = key;
      try {
        if (this.isDispensaLegado(row)) {
          await this.buscarAnexosDispensa(row.id);
          const edital = this.dispensaAnexos.find(anexo => anexo.tipo === 'EDITAL');
          if (edital) window.location = fileServer(edital.arquivo);
        } else {
          await this.buscarOfertaCompraAnexos(row.id);
          const edital = this.ofertaCompraAnexos.find(anexo => anexo.tipo === 'EDITAL');
          if (edital) window.location = fileServer(edital.arquivo);
        }
      } catch (error) {
        this.$alerta('Erro ao buscar edital', 'is-danger');
      } finally {
        this.loadingEditalId = null;
      }
    },
    async createOfertaCompra(tipo) {
      const id = await this.novaOfertaCompra(tipo);
      if (id) {
        this.$router.push({ name: 'PregaoEditar', params: { id } });
      } else {
        this.$alerta('Erro ao criar licitação', 'is-danger');
      }
    },
    agendarToursLicitacoes() {
      this.$nextTick(() => {
        this.tentarIniciarToursLicitacoes();
      });
    },
    ctxTourLicitacoes(extra = {}) {
      return {
        logged: this.logged,
        canCreate: this.canCreate,
        // O painel de filtros é sempre renderizado (sem v-if); não depende de hasLoadedOnce.
        filtrosVisiveis: true,
        persona: this.persona,
        ...extra,
      };
    },
    preparesTourLicitacoes() {
      return {};
    },
    async tentarIniciarToursLicitacoes() {
      if (!this.hasLoadedOnce || this.loading) return;
      if (isTourActive()) return;

      await this.rodarContextoFeatures('licitacoes.lista', this.ctxTourLicitacoes(), {
        prepares: this.preparesTourLicitacoes(),
      });
    },
    async reabrirTutorialLicitacoes(tourId) {
      if (!tourId) return;
      await this.reabrirFeature(tourId, this.ctxTourLicitacoes(), {
        prepares: this.preparesTourLicitacoes(),
      });
    },
  },
  mounted() {
    if (this.persona === 'pregoeiro') this.loadPurchasingUnitOptions();
    this.agendarToursLicitacoes();
    if (typeof IntersectionObserver !== 'undefined' && this.$el) {
      this._licitacoesTourObserver = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.agendarToursLicitacoes();
          }
        },
        { threshold: 0.05 },
      );
      this._licitacoesTourObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    this.ocultarHintsAtivos();
    if (this._licitacoesTourObserver) {
      this._licitacoesTourObserver.disconnect();
      this._licitacoesTourObserver = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.licitacoes-lista__content {
  padding-left: 0;
  padding-right: 0;
}

.licitacoes-lista__criar-group {
  align-items: center;
  display: flex;
  gap: 0.5rem;
}

.licitacoes-lista__tour-help ::v-deep .feature-tour-help__trigger {
  color: #3273dc;
}

.licitacoes-lista__tour-help ::v-deep .icon {
  font-size: 1.5rem !important;
  width: 1.75rem !important;
  height: 1.75rem !important;
}

.licitacoes-lista__tour-help ::v-deep .mdi:before,
.licitacoes-lista__tour-help ::v-deep .icon i {
  font-size: 1.5rem !important;
}

// Menu com a mesma largura do botão — anula o min-width padrão do Bulma (12rem).
.licitacoes-lista__criar {
  ::v-deep .dropdown-trigger,
  ::v-deep .dropdown-trigger .button {
    width: 100%;
  }

  ::v-deep .dropdown-menu {
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  ::v-deep .dropdown-content {
    width: 100%;
  }
}

.licitacoes-lista__container {
  max-width: none;
  padding-left: 1rem;
  padding-right: 1rem;
  width: 100%;
}

.licitacoes-lista__table-wrap {
  overflow-x: auto;
}

.licitacoes-lista__acoes-cell {
  white-space: nowrap;
}

.licitacoes-lista__paginacao::after {
  clear: both;
  content: '';
  display: table;
}

.tabela-licitacoes {
  font-size: 0.96rem;
  min-width: 48rem;

  ::v-deep table {
    table-layout: fixed;
    width: 100%;
  }

  ::v-deep th,
  ::v-deep td {
    vertical-align: middle;
  }
}

@media (max-width: 768px) {
  .tabela-licitacoes {
    min-width: 0;

    // Cards mobile: label numa linha, valor na seguinte (em vez de duas colunas).
    ::v-deep .table-wrapper.has-mobile-cards {
      tr:not(.detail):not(.is-empty):not(.table-footer) {
        td {
          display: block;
          // Coluna única: cada célula ocupa a largura toda (anula os % do desktop).
          max-width: none;
          overflow-wrap: break-word;
          text-align: left !important;
          width: 100%;

          &::before {
            display: block;
            margin-bottom: 0.25rem;
            overflow-wrap: normal;
            padding-right: 0;
            text-align: left;
          }

          // Células sem rótulo (ex.: expandir detalhes).
          &:not([data-label]),
          &[data-label=''] {
            &::before {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
/* Licitação e Modalidade com overflow contido; Situação 22%; Órgão 16%; resto no Objeto. */
.licitacoes-lista__licitacao-cell {
  max-width: 13%;
  min-width: 0;
  overflow-wrap: anywhere;
  width: 13%;
  word-break: break-word;
}

.licitacoes-lista__modalidade-cell {
  max-width: 14%;
  min-width: 0;
  overflow-wrap: anywhere;
  width: 14%;
  word-break: break-word;
}

.licitacoes-lista__comprador-cell {
  max-width: 16%;
  min-width: 0;
  width: 16%;
  word-break: break-word;
}

.licitacoes-lista__situacao-cell {
  max-width: 22%;
  min-width: 0;
  width: 22%;
  word-break: break-word;
}

.licitacoes-lista__situacao-chips {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.licitacoes-lista__objeto-cell {
  min-width: 0;
  word-break: break-word;
}
</style>
